import {
  PsvReviewOutcome,
  PsvReviewStatus,
} from "@api/shared-runtime/psv-review";
import { isOpenItem } from "@/features/credentials/components/primary-source-runs/run-report-dialog/item-status";
import {
  STATE_MEDICAID_EXCLUSIONS_SOURCE_ID,
  type ReportItem,
  type RunReport,
} from "@/features/credentials/components/primary-source-runs/types";

/** Ids of the left navigation entries; also the `section` search value. */
export enum VerificationSectionId {
  Overview = "overview",
  Npi = "npi",
  Dea = "dea",
  StateLicenses = "state-licenses",
  StateExclusions = "state-exclusions",
  Sam = "sam",
  Oig = "oig",
  Ofac = "ofac",
  MedicareOptOut = "medicare-opt-out",
  Other = "other",
  RunHistory = "run-history",
}

/** The server's report families, an API-only enum compared by value. */
enum ReportFamily {
  Identity = "identity",
  Dea = "dea",
  StateLicense = "state-license",
  Exclusion = "exclusion",
  Sanctions = "sanctions",
  MedicareParticipation = "medicare-participation",
}

/** Person-screening sources that share the server's "exclusion" family. */
enum ExclusionSourceId {
  Sam = "sam-exclusions",
  Oig = "oig-leie",
}

/** A section that lists a subset of the run's items. */
export type ItemSectionId = Exclude<
  VerificationSectionId,
  VerificationSectionId.Overview | VerificationSectionId.RunHistory
>;

export type VerificationSection = {
  id: VerificationSectionId;
  label: string;
  /** Heading shown above the section's table. */
  heading: string;
  description: string;
};

export const ITEM_SECTIONS: (VerificationSection & { id: ItemSectionId })[] = [
  {
    id: VerificationSectionId.Npi,
    label: "NPI",
    heading: "NPI",
    description: "National Provider Identifier registry record",
  },
  {
    id: VerificationSectionId.Dea,
    label: "DEA",
    heading: "DEA registrations",
    description: "Drug Enforcement Administration registrations",
  },
  {
    id: VerificationSectionId.StateLicenses,
    label: "State licenses",
    heading: "State licenses",
    description: "Licenses verified with the issuing boards",
  },
  {
    id: VerificationSectionId.StateExclusions,
    label: "State exclusions",
    heading: "State exclusions",
    description: "State Medicaid exclusion lists",
  },
  {
    id: VerificationSectionId.Sam,
    label: "SAM",
    heading: "SAM exclusions",
    description: "System for Award Management exclusions",
  },
  {
    id: VerificationSectionId.Oig,
    label: "OIG",
    heading: "OIG exclusions",
    description: "Office of Inspector General exclusion list",
  },
  {
    id: VerificationSectionId.Ofac,
    label: "OFAC sanctions",
    heading: "OFAC sanctions",
    description: "Office of Foreign Assets Control sanctions lists",
  },
  {
    id: VerificationSectionId.MedicareOptOut,
    label: "Medicare opt-out",
    heading: "Medicare opt-out",
    description: "Medicare participation and opt-out affidavits",
  },
  {
    id: VerificationSectionId.Other,
    label: "Other",
    heading: "Other checks",
    description: "Additional source checks",
  },
];

export const OVERVIEW_SECTION: VerificationSection = {
  id: VerificationSectionId.Overview,
  label: "Overview",
  heading: "Packet overview",
  description: "Every item in this verification run",
};

export const RUN_HISTORY_SECTION: VerificationSection = {
  id: VerificationSectionId.RunHistory,
  label: "Run history",
  heading: "Run history",
  description: "Verification attempts and completed reports",
};

function exclusionSection(sourceId: string | undefined): ItemSectionId {
  switch (sourceId) {
    case STATE_MEDICAID_EXCLUSIONS_SOURCE_ID:
      return VerificationSectionId.StateExclusions;
    case ExclusionSourceId.Sam:
      return VerificationSectionId.Sam;
    case ExclusionSourceId.Oig:
      return VerificationSectionId.Oig;
    default:
      return VerificationSectionId.Other;
  }
}

/** Which navigation entry lists the item. The server's exclusion family covers
 * three sources that the navigation separates by their source check. */
export function itemSectionId({
  item,
  checks,
}: {
  item: ReportItem;
  checks: RunReport["checks"];
}): ItemSectionId {
  switch (String(item.family)) {
    case ReportFamily.Identity:
      return VerificationSectionId.Npi;
    case ReportFamily.Dea:
      return VerificationSectionId.Dea;
    case ReportFamily.StateLicense:
      return VerificationSectionId.StateLicenses;
    case ReportFamily.Sanctions:
      return VerificationSectionId.Ofac;
    case ReportFamily.MedicareParticipation:
      return VerificationSectionId.MedicareOptOut;
    case ReportFamily.Exclusion:
      return exclusionSection(
        checks.find((check) => check.id === item.checkId)?.sourceId,
      );
    default:
      return VerificationSectionId.Other;
  }
}

/** Items grouped by section. The overview lists them all in navigation order,
 * so it reads the same as the menu. */
export function itemsBySection({
  items,
  checks,
}: {
  items: ReportItem[];
  checks: RunReport["checks"];
}): Map<VerificationSectionId, ReportItem[]> {
  const grouped = new Map<VerificationSectionId, ReportItem[]>(
    ITEM_SECTIONS.map((section) => [section.id, []]),
  );
  for (const item of items) {
    grouped.get(itemSectionId({ item, checks }))?.push(item);
  }
  grouped.set(
    VerificationSectionId.Overview,
    ITEM_SECTIONS.flatMap((section) => grouped.get(section.id) ?? []),
  );
  return grouped;
}

export enum SectionStatus {
  Concern = "concern",
  NeedsAttention = "needs-attention",
  Collecting = "collecting",
  Open = "open",
  Accepted = "accepted",
}

function needsAttention(item: ReportItem) {
  return (
    isOpenItem(item) &&
    (item.outcome === PsvReviewOutcome.Issues ||
      item.outcome === PsvReviewOutcome.NotAvailable)
  );
}

/** The most urgent state among the items: a concern, then open items with
 * issues or missing data, then items still collecting, then open valid items;
 * accepted only when every item is. */
export function sectionStatus(items: ReportItem[]): SectionStatus {
  if (items.some((item) => item.decision?.status === PsvReviewStatus.Concern)) {
    return SectionStatus.Concern;
  }
  if (items.some(needsAttention)) {
    return SectionStatus.NeedsAttention;
  }
  if (items.some((item) => item.decision === null)) {
    return SectionStatus.Collecting;
  }
  if (items.some(isOpenItem)) {
    return SectionStatus.Open;
  }
  return SectionStatus.Accepted;
}

export type SectionTally = {
  concerns: number;
  open: number;
  collecting: number;
  total: number;
};

export function tallyItems(items: ReportItem[]): SectionTally {
  return {
    concerns: items.filter(
      (item) => item.decision?.status === PsvReviewStatus.Concern,
    ).length,
    open: items.filter(isOpenItem).length,
    collecting: items.filter((item) => item.decision === null).length,
    total: items.length,
  };
}
